<template>
    <v-dialog
        v-model="dialog"                      
        persistent 
        max-width="800px"
        :fullscreen="$vuetify.breakpoint.xsOnly"
    >
        <v-card>
            <v-card-title class="configTitle"> 
                <v-icon class="mr-5">
                    mdi-cog-outline
                </v-icon>
                <span :class="[$vuetify.breakpoint.smAndUp ? 'text-h5':'fit-text']">Configure Table for {{ activeTab.label }}</span>                
            </v-card-title>
            <v-card-text class="border border-4">
                <v-container>
                    <v-row>
                        <!-- Config Dialog Tabs-->
                        <v-tabs id="configTabs">
                            <v-tab
                                v-if="activeTab.tabId !== 'tickets-clipboard'"
                                href="#general"
                            >
                                General
                            </v-tab>
                            <v-tab-item
                                value="general"
                                transition="false"
                            >
                                <GeneralTabComponent
                                    :key="generalTabKey"
                                    :config="config"                                    
                                    @updateDefaultSort="updateDefaultSort($event)"
                                    @updateAutoRefresh="updateAutoRefresh($event)"        
                                />
                            </v-tab-item>
                               
                            <v-tab
                                v-if="activeTab.tabId !== 'tickets-clipboard'"
                                href="#colors"
                            >
                                Colors
                            </v-tab>
                            <v-tab-item
                                value="colors"
                                transition="false"
                            >
                                <ColorsTabComponent
                                    :colors="localColors"
                                    @updateColors="setColorConfig"
                                />
                            </v-tab-item>
                            <v-tab
                                v-if="this.$vuetify.breakpoint.smAndUp"                              
                                href="#columns"
                            >
                                Columns
                            </v-tab>
                            <v-tab-item
                                value="columns"
                                transition="false"
                            > 
                                <ColumnsTabComponent
                                    :columns="localColumns"
                                    @updateColumnData="updateColumnData($event)"                                    
                                />
                            </v-tab-item>                            
                        </v-tabs>  
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions class="dialogAction">
                <v-spacer />
                <div
                    :class="{ 'bottomNav': !this.$vuetify.breakpoint.smAndUp }"                
                    class="d-flex mt-2"
                >
                    <v-btn
                        :class="[$vuetify.breakpoint.smAndUp ? 'dialogBtn':'mobile-btn']"
                        outlined
                        class="mr-4"
                        @click="dialogCancel"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        :class="[$vuetify.breakpoint.smAndUp ? 'dialogBtn':'mobile-btn']"
                        depressed
                        color="primary"
                        @click="dialogSave"
                    >
                        Save
                    </v-btn>
                </div>                        
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import { getUserOption } from '../../utils/options';
import GeneralTabComponent from './generalTab';
import ColorsTabComponent from './colorsTab';
import ColumnsTabComponent  from './columnsTab';
import { getTicketTypeColors } from '../../utils/ticketTypeColors';
import { defaultTicketFields, defaultOpenTicketsTabOptions, defaultClipboardFields } from '../../services/ticketDataHelpers';

export default {
    components: {
        GeneralTabComponent,
        ColorsTabComponent,
        ColumnsTabComponent        
    },
    props:{
        dialog: {
            type: Boolean,
            default: false
        },       
    },    
    data(){
        return{            
            // Get the default user's options           
            columnData: [],
            columnDataAssigned: false,
            localColors: getTicketTypeColors(),
            localColumns: '',
            generalTabKey: 0,
            config:{},
            autoRefreshMinutes: 0,
            orderBy: '',
            userOptions: []
        };
    },
    computed: {
        activeTab() {
            return this.$store.getters.activeTab;
        },
        getActiveTabObject(){
            const JSONTAB = 'JSON:TAB:' + this.activeTab.tabId;
            const tabJson = getUserOption(JSONTAB);
            return tabJson ? JSON.parse(tabJson) : defaultOpenTicketsTabOptions;
        },
    },
    watch: {
        dialog() {
            this.initTabs();
            this.initColors();
                       
        },       
    },
    created() {
        this.userOptions = this.$store.getters.userOptions;
    },   
    methods:{          
        updateDefaultSort(sort){
            this.orderBy = sort; 
        },
        updateAutoRefresh(autoRefresh){
            this.autoRefreshMinutes = autoRefresh;
        },
        updateColumnData(columnData){           
           
            this.columnData = columnData;
            this.columnDataAssigned = true;
        },
        dialogCancel(){
            // Close dialog on partent component
            this.$emit('closeDialog');
            this.generalTabKey += 1;
            
        },
        dialogSave: async function() {
            // Save user options
            try {
                const updatedFields = this.columnData.map((c) => c.sql_col_name).join(',');

                const options = [];
                const JSONTAB = 'JSON:TAB:' + this.activeTab.tabId;

                let optionValue = this.getActiveTabObject;
                optionValue._TIMESTAMP_ = (new Date()).toISOString();

                const isNotClipboard = this.activeTab.tabId !== 'tickets-clipboard';

                if (isNotClipboard) {
                    // optionValue.config.fields = defaultTicketFields;
                    optionValue.config.autoRefreshMinutes = this.autoRefreshMinutes;
                    optionValue.config.orderBy = this.orderBy;
                    optionValue.config.fields = this.columnDataAssigned ? updatedFields : optionValue.config.fields;

                    const JSONCOLORS = 'JSON:UI-COLORS';
                    options.push([JSONCOLORS, JSON.stringify(this.localColors)]);

                }else{                    
                    // check if updatedFields is not empty
                    let clipboardFields = defaultClipboardFields;
                    if (updatedFields.length !== 0) {
                        clipboardFields = updatedFields ? updatedFields : defaultClipboardFields;
                    }
                    const CLIPBOARD_FIELDS = 'CLIPBOARD_FIELDS';
                    options.push([CLIPBOARD_FIELDS, clipboardFields]);
                }
                              
                options.push([JSONTAB, JSON.stringify(optionValue)]);                

                // Save options to backend via store action
                await this.$store.dispatch('updateUserOptions', options);

                this.$emit('syncTicketTable');
                this.$emit('closeDialog');

                this.$store.dispatch('setSnackbar', {text:'Ticket configuration updated successfully!'});
            } catch (error) {
                this.$store.dispatch('setSnackbar', {color:'error',text:error.message});
                console.error('Error saving ticket table config: ' + error);
            }

            this.columnDataAssigned = false;

        },
        setColorConfig(colors) {
            this.localColors = colors;
        },
        initColors() {
            this.localColors = getTicketTypeColors();
        },
        // Get the options for the current tab
        async initTabs() {
            const JSONTAB = 'JSON:TAB:' + this.activeTab.tabId;
            let options = [];
            options.push([JSONTAB, JSON.stringify(this.getActiveTabObject)]);
            let optionValue = options[0][1];
            optionValue = JSON.parse(optionValue);

            const isNotClipboard = this.activeTab.tabId !== 'tickets-clipboard';
            if (isNotClipboard) {
                // Get the columns for the current tab
                if(optionValue.config?.fields) {
                    this.localColumns = optionValue.config.fields;
                }else{
                    this.localColumns = defaultTicketFields;                
                }
                if(optionValue.config?.autoRefreshMinutes) {
                    this.autoRefreshMinutes = optionValue.config.autoRefreshMinutes;
                }
                if(optionValue.config?.orderBy) {
                    this.orderBy = optionValue.config.orderBy;
                }
            }else{
                let clipboardFields = getUserOption('CLIPBOARD_FIELDS');
                this.localColumns = clipboardFields ? clipboardFields : defaultClipboardFields;
            }

            // Get the default sort for the current tab            
            this.config = optionValue.config;              
        }
    },
};

</script>
<style scoped>
fit-text {
    font-size: 16px;
    font-size: 4vw;
}
.bottomNav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0px;
  height: 80px;
  display: flex;
  background: var(--ll-surface);
  justify-content: center;
  padding: 16px 16px;
  border-top: 1px solid var(--ll-border);

}
.v-btn.mobile-btn{
  width: 134px;
  height: 48px;
  padding: 16px;
}
</style>
